"use client";

import { c as _c } from "react/compiler-runtime";
import { Building, Monitor, User, Sparkles } from "lucide-react";
import { useState } from "react";
import clsx from "clsx";
import { Card, CardContent } from "../ui/card";
import { Box } from "../ui/box";
import { Heading } from "../ui/heading";
import { Text } from "../ui/text";
import { RenderIf } from "./render-if";
import { formatCurrency } from "@/helpers/format-currency";
import { Badge } from "../ui/badge";
import { ParticipantSelector } from "../ui/participant-selector";
import { BorderBeam } from "../ui/border-beam";
import { EnrollNowDialog } from "./enroll-now-dialog";
import { FillingFast } from "./filling-fast";
import { RegistrationCloseTimer } from "./registration-close-timer";
import { Button } from "../ui/button";
import { usePopup } from "@/providers/popup-provider";
type FastFillingScheduleProps = {
  data?: ServiceEvent;
};
export const FastFillingEvent = t0 => {
  const $ = _c(142);
  const {
    data
  } = t0;
  const [quantity, setQuantity] = useState(1);
  const {
    openPopup
  } = usePopup();
  if (!data || !data?.eventDetail) {
    return null;
  }
  const isCampaignOffer = Boolean(data?.isCampaignOfferApplied);
  const isEarlyBirdOfferApplied = data.isEarlyBirdOfferApplied;
  let t1;
  if ($[0] !== data.serviceFlags) {
    t1 = data?.serviceFlags?.includes("HIDE_PRICE_ON_WEBSITE") || false;
    $[0] = data.serviceFlags;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isHiddenPrice = t1;
  const t2 = data?.eventCloseDate;
  let t3;
  if ($[2] !== t2) {
    t3 = <RegistrationCloseTimer closeDate={t2} />;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== isCampaignOffer) {
    t4 = isCampaignOffer && <><div className="pointer-events-none absolute top-0 right-0 h-40 w-40 animate-pulse rounded-bl-full bg-linear-to-br from-orange-400/30 to-transparent" /><div className="pointer-events-none absolute top-0 left-0 h-32 w-32 rounded-br-full bg-linear-to-br from-orange-300/20 to-transparent" /><div className="pointer-events-none absolute right-0 bottom-0 h-24 w-24 rounded-tl-full bg-linear-to-tl from-orange-400/20 to-transparent" /><div className="absolute top-3 right-3 z-10"><Sparkles className="animate-pulse text-orange-500" size={28} /></div><div className="absolute top-8 right-12 -z-1"><Sparkles className="animate-pulse text-orange-400 delay-300" size={16} /></div></>;
    $[4] = isCampaignOffer;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = isCampaignOffer || isEarlyBirdOfferApplied;
  let t6;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <BorderBeam duration={6} size={300} borderWidth={2} className="via-secondary from-transparent to-transparent" />;
    t7 = <BorderBeam duration={6} delay={3} size={300} borderWidth={2} className="via-primary from-transparent to-transparent" />;
    $[6] = t6;
    $[7] = t7;
  } else {
    t6 = $[6];
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t5) {
    t8 = <RenderIf condition={t5}>{t6}{t7}</RenderIf>;
    $[8] = t5;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== data.weekLabel) {
    t9 = <Badge className="bg-primary/20 text-primary transition-transform hover:scale-105">{data.weekLabel}</Badge>;
    $[10] = data.weekLabel;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== data.isEarlyBirdOfferApplied) {
    t10 = data.isEarlyBirdOfferApplied && <Badge className="bg-green-100 text-green-700 transition-transform hover:scale-105 dark:bg-green-950 dark:text-green-300">Early bird</Badge>;
    $[12] = data.isEarlyBirdOfferApplied;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== isCampaignOffer) {
    t11 = isCampaignOffer && <Badge className="animate-pulse bg-linear-to-r from-orange-500 via-orange-600 to-orange-500 text-white shadow-lg transition-all hover:scale-105"><Sparkles size={12} />Spacial Batch Offer - Limited Time!</Badge>;
    $[14] = isCampaignOffer;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t10 || $[17] !== t11 || $[18] !== t9) {
    t12 = <Box className="my-2 flex flex-wrap gap-2">{t9}{t10}{t11}</Box>;
    $[16] = t10;
    $[17] = t11;
    $[18] = t9;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== data.eventName || $[21] !== data.hasMappedService) {
    t13 = data?.hasMappedService && <Heading as="h5" render={<span />} className="mb-2 block font-semibold">{data?.eventName}</Heading>;
    $[20] = data.eventName;
    $[21] = data.hasMappedService;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const t14 = `(${data.numberOfDays} ${data.numberOfDaysLabel})`;
  let t15;
  if ($[23] !== data.formattedDate || $[24] !== t14) {
    t15 = <h3 className="my-2 font-bold">{data.formattedDate} {t14}</h3>;
    $[23] = data.formattedDate;
    $[24] = t14;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  const t16 = data?.eventDetail?.startTime;
  const t17 = data?.eventDetail?.endTime;
  const t18 = data?.eventDetail?.timezone;
  const t19 = data?.eventDetail?.timeDiffInHours;
  let t20;
  if ($[26] !== t19) {
    t20 = <span>({t19} Hrs / Day)</span>;
    $[26] = t19;
    $[27] = t20;
  } else {
    t20 = $[27];
  }
  let t21;
  if ($[28] !== t16 || $[29] !== t17 || $[30] !== t18 || $[31] !== t20) {
    t21 = <Text className="text-muted-foreground mb-2">{t16} - {t17}{" "}{t18}{t20}</Text>;
    $[28] = t16;
    $[29] = t17;
    $[30] = t18;
    $[31] = t20;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] !== data.modeOfTraining) {
    t22 = data?.modeOfTraining === "online" ? <Monitor className="text-primary" size={20} /> : <Building className="text-primary" size={20} />;
    $[33] = data.modeOfTraining;
    $[34] = t22;
  } else {
    t22 = $[34];
  }
  const t23 = data?.modeOfTraining;
  let t24;
  if ($[35] !== t23) {
    t24 = <Text>{t23}</Text>;
    $[35] = t23;
    $[36] = t24;
  } else {
    t24 = $[36];
  }
  let t25;
  if ($[37] !== t22 || $[38] !== t24) {
    t25 = <Box className="mb-2 flex items-center space-x-2">{t22}{t24}</Box>;
    $[37] = t22;
    $[38] = t24;
    $[39] = t25;
  } else {
    t25 = $[39];
  }
  let t26;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <b>Exam : </b>;
    $[40] = t26;
  } else {
    t26 = $[40];
  }
  const t27 = data.withExam ? "Included" : "Not Included";
  let t28;
  if ($[41] !== t27) {
    t28 = <Box className="mb-2 flex items-center space-x-2">{t26}<Text>{t27}</Text></Box>;
    $[41] = t27;
    $[42] = t28;
  } else {
    t28 = $[42];
  }
  let t29;
  if ($[43] !== data.serviceFlags) {
    t29 = data?.serviceFlags?.includes("IS_LAB") && <Box className="mb-2 flex items-center space-x-2"><b>Lab : </b><Text>Included</Text></Box>;
    $[43] = data.serviceFlags;
    $[44] = t29;
  } else {
    t29 = $[44];
  }
  let t30;
  if ($[45] !== data.serviceFlags) {
    t30 = data?.serviceFlags?.includes("HAS_MEMBERSHIP");
    $[45] = data.serviceFlags;
    $[46] = t30;
  } else {
    t30 = $[46];
  }
  const t31 = t30 as boolean;
  let t32;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <b>Membership : </b>;
    $[47] = t32;
  } else {
    t32 = $[47];
  }
  const t33 = data.serviceFlags?.includes("HAS_MEMBERSHIP") ? "Yes" : "No";
  let t34;
  if ($[48] !== t33) {
    t34 = <Box className="mb-2 flex items-center space-x-2">{t32}<Text>{t33}</Text></Box>;
    $[48] = t33;
    $[49] = t34;
  } else {
    t34 = $[49];
  }
  let t35;
  if ($[50] !== t31 || $[51] !== t34) {
    t35 = <RenderIf condition={t31}>{t34}</RenderIf>;
    $[50] = t31;
    $[51] = t34;
    $[52] = t35;
  } else {
    t35 = $[52];
  }
  let t36;
  if ($[53] !== data.eventDetail.trainer) {
    t36 = data?.eventDetail?.trainer && <Box className="mb-2 flex items-center space-x-2"><User className="text-primary" size={20} /><Text>By {data?.eventDetail?.trainer?.name}</Text></Box>;
    $[53] = data.eventDetail.trainer;
    $[54] = t36;
  } else {
    t36 = $[54];
  }
  const t37 = !isHiddenPrice;
  let t38;
  if ($[55] !== isCampaignOffer) {
    t38 = clsx("mt-3 flex items-center justify-between gap-2", {
      "rounded-xl border-2 border-orange-300 bg-linear-to-r from-orange-50 via-orange-100/80 to-orange-50 p-4 shadow-md": isCampaignOffer
    });
    $[55] = isCampaignOffer;
    $[56] = t38;
  } else {
    t38 = $[56];
  }
  let t39;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <span />;
    $[57] = t39;
  } else {
    t39 = $[57];
  }
  let t40;
  if ($[58] !== isCampaignOffer) {
    t40 = clsx("text-xl font-bold", {
      "text-orange-600": isCampaignOffer
    });
    $[58] = isCampaignOffer;
    $[59] = t40;
  } else {
    t40 = $[59];
  }
  const t41 = data.eventDetail.price * quantity;
  let t42;
  if ($[60] !== data.eventDetail.currency || $[61] !== t41) {
    t42 = formatCurrency(t41, data.eventDetail.currency);
    $[60] = data.eventDetail.currency;
    $[61] = t41;
    $[62] = t42;
  } else {
    t42 = $[62];
  }
  let t43;
  if ($[63] !== t40 || $[64] !== t42) {
    t43 = <Heading as="h5" render={t39} className={t40}>{t42}</Heading>;
    $[63] = t40;
    $[64] = t42;
    $[65] = t43;
  } else {
    t43 = $[65];
  }
  let t44;
  if ($[66] !== data.eventDetail.currency || $[67] !== data.eventDetail.discountPercentNumber || $[68] !== data.eventDetail.discountValue || $[69] !== data.eventDetail.offerDiscountType || $[70] !== isCampaignOffer) {
    t44 = isCampaignOffer && data.eventDetail.discountValue > 0 && <span className="animate-pulse rounded-full bg-linear-to-r from-orange-500 to-orange-600 px-3 py-1 text-xs font-bold text-white shadow-md">Save {data.eventDetail.discountPercentNumber}{" "}{data.eventDetail.offerDiscountType === "Percentage" ? "%" : data.eventDetail.currency}</span>;
    $[66] = data.eventDetail.currency;
    $[67] = data.eventDetail.discountPercentNumber;
    $[68] = data.eventDetail.discountValue;
    $[69] = data.eventDetail.offerDiscountType;
    $[70] = isCampaignOffer;
    $[71] = t44;
  } else {
    t44 = $[71];
  }
  let t45;
  if ($[72] !== t43 || $[73] !== t44) {
    t45 = <Box className="flex flex-wrap items-center gap-3">{t43}{t44}</Box>;
    $[72] = t43;
    $[73] = t44;
    $[74] = t45;
  } else {
    t45 = $[74];
  }
  const t46 = data.eventDetail.discountValue > 0;
  let t47;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <span />;
    $[75] = t47;
  } else {
    t47 = $[75];
  }
  const t48 = !isCampaignOffer;
  let t49;
  if ($[76] !== isCampaignOffer || $[77] !== t48) {
    t49 = clsx("text-base font-bold line-through", {
      "text-gray-500": t48,
      "font-semibold text-orange-500": isCampaignOffer
    });
    $[76] = isCampaignOffer;
    $[77] = t48;
    $[78] = t49;
  } else {
    t49 = $[78];
  }
  const t50 = (data.eventDetail.price + data.eventDetail.discountValue) * quantity;
  let t51;
  if ($[79] !== data.eventDetail.currency || $[80] !== t50) {
    t51 = formatCurrency(t50, data.eventDetail.currency);
    $[79] = data.eventDetail.currency;
    $[80] = t50;
    $[81] = t51;
  } else {
    t51 = $[81];
  }
  let t52;
  if ($[82] !== t49 || $[83] !== t51) {
    t52 = <Heading as="h5" render={t47} className={t49}>{t51}</Heading>;
    $[82] = t49;
    $[83] = t51;
    $[84] = t52;
  } else {
    t52 = $[84];
  }
  let t53;
  if ($[85] !== t46 || $[86] !== t52) {
    t53 = <RenderIf condition={t46}>{t52}</RenderIf>;
    $[85] = t46;
    $[86] = t52;
    $[87] = t53;
  } else {
    t53 = $[87];
  }
  let t54;
  if ($[88] !== t45 || $[89] !== t53) {
    t54 = <Box className="flex flex-1 flex-col">{t45}{t53}</Box>;
    $[88] = t45;
    $[89] = t53;
    $[90] = t54;
  } else {
    t54 = $[90];
  }
  let t55;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t55 = <ParticipantSelector min={1} noLabel={true} onChange={setQuantity} />;
    $[91] = t55;
  } else {
    t55 = $[91];
  }
  let t56;
  if ($[92] !== t38 || $[93] !== t54) {
    t56 = <Box className={t38}>{t54}{t55}</Box>;
    $[92] = t38;
    $[93] = t54;
    $[94] = t56;
  } else {
    t56 = $[94];
  }
  let t57;
  if ($[95] !== t37 || $[96] !== t56) {
    t57 = <RenderIf condition={t37}>{t56}</RenderIf>;
    $[95] = t37;
    $[96] = t56;
    $[97] = t57;
  } else {
    t57 = $[97];
  }
  let t58;
  if ($[98] !== data.eventDetail.discountPercent || $[99] !== data.eventDetail.discountValue) {
    t58 = data.eventDetail.discountValue && <span className="text-sm font-medium text-green-600">{data.eventDetail.discountPercent} Discount + 4 % Processing Fees Waiver</span>;
    $[98] = data.eventDetail.discountPercent;
    $[99] = data.eventDetail.discountValue;
    $[100] = t58;
  } else {
    t58 = $[100];
  }
  let t59;
  if ($[101] !== t58) {
    t59 = <Box className="mt-2">{t58}</Box>;
    $[101] = t58;
    $[102] = t59;
  } else {
    t59 = $[102];
  }
  let t60;
  if ($[103] !== data.isEarlyBirdOfferApplied || $[104] !== t59) {
    t60 = <RenderIf condition={data.isEarlyBirdOfferApplied}>{t59}</RenderIf>;
    $[103] = data.isEarlyBirdOfferApplied;
    $[104] = t59;
    $[105] = t60;
  } else {
    t60 = $[105];
  }
  const t61 = !isHiddenPrice;
  let t62;
  if ($[106] !== data.serviceId || $[107] !== openPopup) {
    t62 = <Button className="w-full" onClick={() => openPopup({
      type: "enquiry",
      title: "Enquire About Course & Pricing",
      description: "Get detailed information about course pricing, upcoming batch dates, and training schedules.",
      data: {
        serviceId: data.serviceId,
        submitLabel: "Enquire Now"
      }
    })}>Enquire Now</Button>;
    $[106] = data.serviceId;
    $[107] = openPopup;
    $[108] = t62;
  } else {
    t62 = $[108];
  }
  let t63;
  if ($[109] === Symbol.for("react.memo_cache_sentinel")) {
    t63 = {
      className: "mt-3 w-full"
    };
    $[109] = t63;
  } else {
    t63 = $[109];
  }
  let t64;
  if ($[110] !== data || $[111] !== isCampaignOffer || $[112] !== quantity) {
    t64 = <EnrollNowDialog isCampaignOffer={isCampaignOffer} participant={quantity} buttonProps={t63} course={data.serviceName} date={data.formattedDate} data={data} />;
    $[110] = data;
    $[111] = isCampaignOffer;
    $[112] = quantity;
    $[113] = t64;
  } else {
    t64 = $[113];
  }
  let t65;
  if ($[114] !== t61 || $[115] !== t62 || $[116] !== t64) {
    t65 = <RenderIf condition={t61} fallback={t62}>{t64}</RenderIf>;
    $[114] = t61;
    $[115] = t62;
    $[116] = t64;
    $[117] = t65;
  } else {
    t65 = $[117];
  }
  let t66;
  if ($[118] === Symbol.for("react.memo_cache_sentinel")) {
    t66 = <FillingFast className="mt-3" />;
    $[118] = t66;
  } else {
    t66 = $[118];
  }
  let t67;
  if ($[119] !== data.isFastFilling) {
    t67 = <RenderIf condition={data.isFastFilling}>{t66}</RenderIf>;
    $[119] = data.isFastFilling;
    $[120] = t67;
  } else {
    t67 = $[120];
  }
  let t68;
  if ($[121] !== t12 || $[122] !== t13 || $[123] !== t15 || $[124] !== t21 || $[125] !== t25 || $[126] !== t28 || $[127] !== t29 || $[128] !== t35 || $[129] !== t36 || $[130] !== t57 || $[131] !== t60 || $[132] !== t65 || $[133] !== t67) {
    t68 = <CardContent>{t12}{t13}{t15}{t21}{t25}{t28}{t29}{t35}{t36}{t57}{t60}{t65}{t67}</CardContent>;
    $[121] = t12;
    $[122] = t13;
    $[123] = t15;
    $[124] = t21;
    $[125] = t25;
    $[126] = t28;
    $[127] = t29;
    $[128] = t35;
    $[129] = t36;
    $[130] = t57;
    $[131] = t60;
    $[132] = t65;
    $[133] = t67;
    $[134] = t68;
  } else {
    t68 = $[134];
  }
  let t69;
  if ($[135] !== t4 || $[136] !== t68 || $[137] !== t8) {
    t69 = <Card className="relative overflow-hidden shadow-md transition-all duration-300">{t4}{t8}{t68}</Card>;
    $[135] = t4;
    $[136] = t68;
    $[137] = t8;
    $[138] = t69;
  } else {
    t69 = $[138];
  }
  let t70;
  if ($[139] !== t3 || $[140] !== t69) {
    t70 = <span className="relative">{t3}{t69}</span>;
    $[139] = t3;
    $[140] = t69;
    $[141] = t70;
  } else {
    t70 = $[141];
  }
  return t70;
};